import React, { useEffect } from 'react';
import { Bell, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { SearchInput } from "../ui/SearchInput";
import { Badge } from "../ui/badge";
import { useAuthActions, useAuth } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import NotificationDrawer from '../notifications/NotificationDrawer';
import { useNotificationStore } from '../../stores/notificationStore';

export function Header() {
  const { user, caProfile } = useAuth();
  const logout = useAuthActions(state => state.logout);
  const navigate = useNavigate();
  const { unreadCount, initialize, cleanup } = useNotificationStore();

  useEffect(() => {
    // Initialize notifications only once on mount
    initialize();
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-50 px-8 flex items-center justify-between shadow-sm">
      <div className="w-96">
        <SearchInput
          placeholder="Search clients, GSTIN, or documents..."
          className="w-full"
        />
      </div>

      <div className="flex items-center gap-6">
        <NotificationDrawer>
          <button 
            className="relative text-slate-500 hover:text-slate-700 transition-colors outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg p-2"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-2 w-2 p-0 border-0"
                aria-label="Unread notifications"
              />
            )}
          </button>
        </NotificationDrawer>

        <div className="pl-6 border-l border-slate-200">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-3 cursor-pointer outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg p-1">
                        <Avatar className="h-8 w-8">
                            <AvatarImage src="" alt={caProfile?.firmName || "CA"} />
                            <AvatarFallback className="bg-emerald-100 text-emerald-700">
                                {(caProfile?.firmName || "CA").substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div className="hidden md:block text-left">
                            <p className="text-sm font-medium text-slate-900 leading-none">{caProfile?.firmName || 'Kavy Solutions'}</p>
                            <p className="text-xs text-slate-500 mt-1">Managing Partner</p>
                        </div>
                        <ChevronDown className="w-4 h-4 text-slate-400" aria-hidden="true" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/ca/settings')} className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/ca/settings')} className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Log out</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
